import React, { useCallback, useEffect } from 'react';

import { Danger, Edit } from '@randstad-lean-mobile-factory/react-assets/dist/icons';
import { ServerErrorYellowSmall } from '@randstad-lean-mobile-factory/react-assets/dist/illustrations';
import {
  Button,
  Loader,
  Modal,
  ModalActions,
  ModalContent,
  WithLightTitle,
} from '@randstad-lean-mobile-factory/react-components-core';
import { TextInput, useFormWithZodResolver } from '@randstad-lean-mobile-factory/react-form-fields';
import { z } from 'zod';

import SearchableDropDown from '../../../Components/SearchableDropDown';
import { useDeleteRoutingById } from '../../../Hooks/Routings/useDeleteRoutingById';
import { useFetchAMAgencies } from '../../../Hooks/Routings/useFetchAMAgencies';
import { useFetchRoutingById } from '../../../Hooks/Routings/useFetchRoutingById';
import { useUpdateRouting } from '../../../Hooks/Routings/useUpdateRouting';
import { AppelMedicalEventSource, UpdateRoutingInfoBody } from '../../../Services/API';
import { brandCodes } from '../../AdminPage/RoutingsSection/RoutingsSection.types';

import styles from './EditRoutingsModal.module.scss';
import { Props } from './EditRoutingsModal.types';

const EditRoutingsModalComponent = ({ routingId }: Props) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const schema = z.object({
    siret: z
      .string()
      .regex(/^\d{3} \d{3} \d{3} \d{3} \d{2}$/, 'ex: XXX XXX XXX XXX XX')
      .max(18, '14 caractères maximum')
      .optional(),
    agencyId: z.string().optional(),
    brandCode: z.string().optional(),
    service: z.string().max(150, '150 caractères maximum').optional(),
  });
  const {
    data: routingInfos,
    isSuccess,
    isLoading,
  } = useFetchRoutingById(routingId, { enabled: isOpen });

  const { control, setValue, watch, handleSubmit } = useFormWithZodResolver({
    schema,
    defaultValues: {
      siret: undefined,
      agencyId: undefined,
      brandCode: undefined,
      service: undefined,
    },
  });
  const siret = watch('siret');

  const editRoutingMutation = useUpdateRouting();
  const deleteRoutingMutation = useDeleteRoutingById();

  const formatSiret = useCallback((value?: string) => {
    if (!value) return '';
    return value
      .replace(/\D/g, '')
      .slice(0, 14)
      .replace(/(\d{3})(?=\d)/g, '$1 ')
      .trim();
  }, []);
  useEffect(() => {
    setValue('siret', formatSiret(siret));
  }, [formatSiret, setValue, siret]);

  useEffect(() => {
    if (isSuccess) {
      setValue('agencyId', routingInfos.agencyId);
      setValue('brandCode', routingInfos.brandCode);
      setValue('siret', routingInfos.siret);
      setValue('service', routingInfos?.service);
    }
  }, [isSuccess, routingInfos, setValue]);
  const { data: agencies } = useFetchAMAgencies({ enabled: isOpen });

  return (
    <>
      <Button
        variant="ghost"
        onClick={() => {
          setIsOpen(true);
        }}
      >
        <Edit variant="line" />
      </Button>
      <Modal size="medium" open={isOpen} onClose={() => setIsOpen(false)}>
        {
          <>
            <ModalContent title="modifier le routage">
              <div className={styles.dangerContainer}>
                <Danger variant="line" color="danger" />
                <p>
                  Important, dans le cadre d’une modification d’une agence vers une autre agence,
                  vous devez ré-renseigner toutes les tables de correspondance
                </p>
              </div>

              {isSuccess ? (
                <div className={styles.inputContainer}>
                  <div className={styles.sectionTitle}>infos du mail</div>
                  <div className={styles.inputRow}>
                    <WithLightTitle title="qualification" className={styles.inputElement}>
                      {routingInfos.qualification}
                    </WithLightTitle>
                    <WithLightTitle title="nom du client" className={styles.inputElement}>
                      {routingInfos.clientName}
                    </WithLightTitle>
                  </div>
                  <div className={styles.inputRow}>
                    <WithLightTitle title="service" className={styles.inputElement}>
                      <TextInput
                        name="service"
                        placeholder="ex: Urgences, SMUR, etc."
                        disabled={routingInfos?.source === AppelMedicalEventSource.ARTEMIHS}
                        control={control}
                      />
                    </WithLightTitle>
                  </div>
                  <div className={styles.sectionTitle}>infos osmose</div>
                  <div className={styles.inputRow}>
                    <WithLightTitle title="siret" className={styles.inputElement}>
                      <TextInput
                        name="siret"
                        placeholder="ex: XXX XXX XXX XXX XX"
                        control={control}
                      />
                    </WithLightTitle>
                    <WithLightTitle title="code agence" className={styles.inputElement}>
                      <SearchableDropDown
                        items={agencies ?? []}
                        selectedItem={watch('agencyId')}
                        keyValueExtractor={item => {
                          return {
                            key: item,
                            label: item,
                            value: item,
                            item,
                          };
                        }}
                        onSelectItem={item => {
                          if (!item) return;
                          setValue('agencyId', item);
                        }}
                        placeholder="sélectionner une agence"
                      />
                    </WithLightTitle>
                  </div>
                  <div className={styles.inputRow}>
                    <WithLightTitle title="marque" className={styles.inputElement}>
                      <SearchableDropDown
                        items={brandCodes}
                        selectedItem={watch('brandCode')}
                        keyValueExtractor={item => {
                          return {
                            key: item,
                            label: item,
                            value: item,
                            item,
                          };
                        }}
                        onSelectItem={item => {
                          if (!item) return;
                          setValue('brandCode', item);
                        }}
                        placeholder="sélectionner une marque"
                      />
                    </WithLightTitle>
                  </div>
                </div>
              ) : (
                <>
                  {isLoading ? (
                    <div className={styles.illustration}>
                      <Loader size="medium" />
                      <div>chargement des données</div>
                    </div>
                  ) : (
                    <div className={styles.illustration}>
                      <ServerErrorYellowSmall />
                      désolé, une erreur s'est produite pendant le chargement des données
                    </div>
                  )}
                </>
              )}
            </ModalContent>
            <ModalActions side="left">
              <Button
                type="submit"
                variant="tertiary"
                onClick={() => {
                  deleteRoutingMutation.mutate(routingId, {
                    onSuccess: () => {
                      setIsOpen(false);
                    },
                  });
                }}
              >
                supprimer le routage
              </Button>
            </ModalActions>
            <ModalActions side="right">
              <Button
                variant="secondary"
                onClick={() => {
                  setIsOpen(false);
                }}
              >
                annuler
              </Button>
              <Button
                type="submit"
                variant="primary"
                mutationStatus={editRoutingMutation.status}
                disabled={!isSuccess}
                onClick={handleSubmit(data => {
                  editRoutingMutation.mutate(
                    {
                      ...routingInfos,
                      ...data,
                      siret: data.siret?.replace(/\s+/g, ''),
                    } as UpdateRoutingInfoBody,
                    {
                      onSuccess: () => {
                        setTimeout(() => {
                          editRoutingMutation.reset();
                          setIsOpen(false);
                        }, 500);
                      },
                      onError: () => {
                        setTimeout(() => {
                          editRoutingMutation.reset();
                        }, 500);
                      },
                    }
                  );
                })}
              >
                valider
              </Button>
            </ModalActions>
          </>
        }
      </Modal>
    </>
  );
};

export default EditRoutingsModalComponent;
